import { createCustomEqual } from 'fast-equals';
import { isLatLngLiteral } from '@googlemaps/typescript-guards';
import {
  LocationMarker,
  LocationMarkerType,
  LiveLocationMarker
} from '../components/TwMarker/TwMarker';
import { LoadLocation } from '../redux/api/load/load.model';

export const deepCompareEqualsForMaps = createCustomEqual((deepEqual) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (a: any, b: any): boolean => {
    if (
      isLatLngLiteral(a) ||
      a instanceof google.maps.LatLng ||
      isLatLngLiteral(b) ||
      b instanceof google.maps.LatLng
    ) {
      return new google.maps.LatLng(a).equals(new google.maps.LatLng(b));
    }

    return deepEqual(a, b);
  }
);

export const getMarkerIcon = (type: LocationMarkerType): google.maps.Icon | undefined => {
  const url = window.origin;

  switch (type) {
    case LocationMarkerType.LOCATION_PIN:
      return {
        url: `${url}/pin-origin.png`
      };
    case LocationMarkerType.STOP_SQUARE:
      return {
        url: `${url}/square.png`
      };
    case LocationMarkerType.STOP_CIRCLE:
      return {
        url: `${url}/circle.png`
      };
    case LocationMarkerType.LIVE_LOCATION:
      return {
        url: `${url}/location.png`
      };
    default:
      return undefined;
  }
};

/**
 * Returns a query url for a Google Maps search of a place
 * @param location Load location object
 * @returns
 */
export const getMapsSearchUrl = (
  location: LoadLocation,
  enableAddressLocationLink: boolean
): string => {
  if (!location.address_1 && !location.latitude && !location.longitude) return '';

  let encodedAddress = '';

  if (location.full_address) {
    encodedAddress = encodeURI(location.full_address);
  } else {
    encodedAddress = encodeURI(location.address_1);
  }

  const queryLatLng =
    location.latitude && location.longitude
      ? `&query=${location.latitude}%2C${location.longitude}`
      : '';
  const queryAddress = `&query=${encodedAddress}`;

  if (enableAddressLocationLink && queryAddress) {
    return `https://www.google.com/maps/search/?api=1${queryAddress}`;
  }
  if (
    (enableAddressLocationLink && !queryAddress && queryLatLng) ||
    (!enableAddressLocationLink && queryLatLng)
  ) {
    return `https://www.google.com/maps/search/?api=1${queryLatLng}`;
  }

  return `https://www.google.com/maps/search/?api=1${queryAddress}`;
};

/**
 * Returns a geo link for a location - used if on mobile flutter app
 * @param location Load location object
 * @param enableAddressLocationLink boolean to enable address location link
 * @returns string
 */
export const getGeoLink = (location: LoadLocation, enableAddressLocationLink: boolean): string => {
  if (!location.address_1 && !location.latitude && !location.longitude) return '';

  let encodedAddress = '';

  // Encode the full address if available, otherwise fallback to address_1
  if (location.full_address) {
    encodedAddress = encodeURI(location.full_address);
  } else if (location.address_1) {
    encodedAddress = encodeURI(location.address_1);
  }

  // Generate the geo link based on the latitude and longitude or the address
  const geoLatLng =
    location.latitude && location.longitude ? `geo:${location.latitude},${location.longitude}` : '';

  const geoAddress = encodedAddress ? `geo:0,0?q=${encodedAddress}` : '';

  // Return the appropriate geo link
  if (enableAddressLocationLink && geoAddress) {
    return geoAddress;
  }
  if (
    (enableAddressLocationLink && !geoAddress && geoLatLng) ||
    (!enableAddressLocationLink && geoLatLng)
  ) {
    return geoLatLng;
  }

  return geoAddress || geoLatLng;
};

/**
 * helper function to get live location marker
 */
export const getLiveLocationMarker = (
  lat: number,
  lng: number,
  timestamp: string,
  lastUpdatedTimestamp: string
): LiveLocationMarker => {
  return {
    position: new google.maps.LatLng({
      lat: lat,
      lng: lng
    }),
    type: LocationMarkerType.LIVE_LOCATION,
    timestamp: timestamp,
    lastUpdatedTimestamp: lastUpdatedTimestamp
  };
};

/**
 * helper function go create list of location markers
 * */
export const getMapMarkers = (
  start: LoadLocation | undefined,
  end: LoadLocation | undefined,
  intermediateStops: LoadLocation[] | undefined,
  startOrder: number
): LocationMarker[] => {
  if (
    !!start &&
    !!start.latitude &&
    !!start.longitude &&
    !!end &&
    !!end.latitude &&
    !!end.longitude
  ) {
    // Render markers
    const pickupMarker: LocationMarker = {
      position: new google.maps.LatLng({
        lat: start.latitude,
        lng: start.longitude
      }),
      type: LocationMarkerType.STOP_CIRCLE,
      order: startOrder
    };
    const deliveryMarker: LocationMarker = {
      position: new google.maps.LatLng({
        lat: end.latitude,
        lng: end.longitude
      }),
      type: LocationMarkerType.STOP_SQUARE,
      order: startOrder + (intermediateStops ? intermediateStops?.length + 1 : 0)
    };

    const stopsMarkers: LocationMarker[] = [];

    if (intermediateStops && intermediateStops.length > 0) {
      intermediateStops.forEach((stop, index) => {
        stop?.latitude &&
          stop?.longitude &&
          stopsMarkers.push({
            position: new google.maps.LatLng({
              lat: stop.latitude,
              lng: stop.longitude
            }),
            type: LocationMarkerType.STOP_CIRCLE,
            order: startOrder + index + 1
          });
      });
    }

    return [pickupMarker, ...stopsMarkers, deliveryMarker];
  } else return [];
};

// Example pin: https://www.google.com/maps/@37.9631397,-121.3764142,11.79z
// Example pin: https://www.google.com/maps/search/32.929873,+-117.052411
// Example place: https://www.google.com/maps/place/Sacramento+International+Airport/@38.6950896,-121.5926397,17z/data=!3m1!4b1!4m5!3m4!1s0x809b2b73ce6a70ad:0xa460901228ef4232!8m2!3d38.6950854!4d-121.5900648
// Example place: https://www.google.com/maps/place/Costco+Wholesale/@32.9161118,-117.0645992,13z/data=!4m5!3m4!1s0x80dbfba0bd58f981:0xb3664fa67ad4658d!8m2!3d32.9369239!4d-117.0329895
export const validateGoogleMapsLink = (link: string): boolean =>
  /^https:\/\/www\.google\.com\/maps\/.*\d+[.]?\d*,[+-]*\d+[.]?\d*.*/.test(link);

export const getLatLngFromPlaceUrl = (originalLink: string): google.maps.LatLng | null => {
  // remove query params from url
  const urlBeforeQueryParams = originalLink.split('?')[0];

  const urlPattern = /!3d(?<latitude>[^!]+)!4d(?<longitude>[^!]+)/gm;
  const match = urlBeforeQueryParams.match(urlPattern);
  if (!match) return null;

  const latitude = Array.from(urlBeforeQueryParams.matchAll(urlPattern)).map(
    ({ groups }) => groups?.latitude
  )[0];
  const longitude = Array.from(urlBeforeQueryParams.matchAll(urlPattern)).map(
    ({ groups }) => groups?.longitude
  )[0];

  return new google.maps.LatLng({
    lat: Number(latitude),
    lng: Number(longitude)
  });
};

export const getLatLongFromPinUrl = (link: string): google.maps.LatLng | null => {
  const match = link.match(/\d+[.]?\d*,[+-]*\d+[.]?\d*/);

  if (!match) return null;

  const split = match[0].replace('@', '').replace('/', '').split(',');

  if (split.length < 2) return null;

  if (split[1][1] == '+' || split[1][1] == '-') {
    split[1] = split[1].slice(1);
  }

  return new google.maps.LatLng({
    lat: Number(split[0]),
    lng: Number(split[1])
  });
};

export const getLatLngFromGoogleMapsLink = (link: string): google.maps.LatLng | null => {
  const latLong = getLatLngFromPlaceUrl(link);
  if (latLong) return latLong;

  return getLatLongFromPinUrl(link);
};
